import {AfterViewChecked, AfterViewInit, Component, inject, Input, OnDestroy, OnInit} from "@angular/core";
import {Chart, ChartConfiguration, ChartDataset, registerables, TooltipItem} from "chart.js";
import {Store} from "@ngrx/store";
import {
    selectBaseScenarioDealCashflowsGraph,
    selectDealCashflowsGraph,
    selectHighScenarioDealCashflowsGraph,
    selectLowScenarioDealCashflowsGraph, selectWeightedScenarioDealCashflowsGraph
} from "../../store/deal/deal.selectors";
import {
    selectDealSensitivityDetailsBaseScenarioCashflowsGraph,
    selectDealSensitivityDetailsHighScenarioCashflowsGraph,
    selectDealSensitivityDetailsLowScenarioCashflowsGraph,
    selectDealSensitivityDetailsWeightedScenarioCashflowsGraph
} from "../../deal-sensitivity-page/store/deal-sensitivity.selectors";
import {map, Observable, Subscription} from "rxjs";
import {DateTime} from "luxon";
import {DealCashflowGraphData} from "../../utils/deal-cashflows-util";
import {CardChartSource} from "../../../shared/comments/card-context-menu/card-menu.types";
import {chartImageSource} from "../../../shared/comments/card-context-menu/chart-image-source.util";

@Component({
    selector: "valumize-deal-cashflows-graph",
    templateUrl: "./deal-cashflows-graph.component.html",
    styleUrls: ["./deal-cashflows-graph.component.scss"],
    standalone: false
})
export class DealCashflowsGraphComponent implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy {
    private readonly store = inject(Store);

    @Input() isSensitivityView = false;

    dealCashflowsGraph$ = this.store.select(selectDealCashflowsGraph);
    lowScenarioCashflowsGraph$!: Observable<DealCashflowGraphData>;
    baseScenarioCashflowsGraph$!: Observable<DealCashflowGraphData>;
    highScenarioCashflowsGraph$!: Observable<DealCashflowGraphData>;
    weightedScenarioCashflowsGraph$!: Observable<DealCashflowGraphData>;

    ngOnInit(): void {
        this.lowScenarioCashflowsGraph$ = this.isSensitivityView
            ? this.store.select(selectDealSensitivityDetailsLowScenarioCashflowsGraph)
            : this.store.select(selectLowScenarioDealCashflowsGraph);
        this.baseScenarioCashflowsGraph$ = this.isSensitivityView
            ? this.store.select(selectDealSensitivityDetailsBaseScenarioCashflowsGraph)
            : this.store.select(selectBaseScenarioDealCashflowsGraph);
        this.highScenarioCashflowsGraph$ = this.isSensitivityView
            ? this.store.select(selectDealSensitivityDetailsHighScenarioCashflowsGraph)
            : this.store.select(selectHighScenarioDealCashflowsGraph);
        this.weightedScenarioCashflowsGraph$ = this.isSensitivityView
            ? this.store.select(selectDealSensitivityDetailsWeightedScenarioCashflowsGraph)
            : this.store.select(selectWeightedScenarioDealCashflowsGraph);
    }

    subscriptions: Subscription[] = [];
    graphPanelOpenState = true;
    scenarioGraphPanelOpenState = true;

    private chart: Chart | undefined;
    private lowChart: Chart | undefined;
    private baseChart: Chart | undefined;
    private highChart: Chart | undefined;
    private weightedChart: Chart | undefined;

    /** Card-menu image source: the combined cashflows chart (falls back to the weighted scenario). */
    readonly cardChartSource: CardChartSource = chartImageSource(() => this.chart ?? this.weightedChart);

    constructor( ) {
    }

    ngAfterViewInit() {
        Chart.register(...registerables);

        // Main aggregate chart is hidden in sensitivity view; skip its setup so we
        // don't attempt new Chart(null, ...) when the canvas isn't rendered.
        if (this.isSensitivityView) {
            return;
        }

        const ctx = document.getElementById("DealCashflowsGraph") as HTMLCanvasElement;

        this.subscriptions.push(this.dealCashflowsGraph$.pipe(map(graph => {
                if (this.chart) {
                    this.chart.destroy();
                }
                if (this.hasAggregateGraphData(graph)) {
                    this.chart = new Chart(ctx, {
                        type: "line",
                        data: graph,
                        options: {
                            responsive: true,
                            maintainAspectRatio: false,
                            animation: false,
                            scales: {
                                x: {
                                    type: "time",
                                    time: {
                                        unit: "year", // Set the interval granularity; can also be set to quarter
                                        displayFormats: {
                                            quarter: "MMM yyyy"
                                        },
                                        tooltipFormat: "MMM yyyy"
                                    },
                                    ticks: {
                                        source: "auto",
                                    },
                                    bounds: "ticks"
                                },
                                y: {
                                    beginAtZero: true,
                                    grid: {
                                        color: (context) => {
                                            if (context.tick.value === 0) {
                                                return "rgba(0,0,0,0.2)";
                                            }
                                            return "rgba(0,0,0,0.1)";
                                        },
                                        lineWidth: (context) => context.tick.value === 0 ? 3 : 1
                                    }
                                }
                            }
                        }
                    });
                }
            })).subscribe()
        );
    }

    ngAfterViewChecked() {
        const lowCtx = document.getElementById("LowScenarioCashflowsGraph") as HTMLCanvasElement;
        const baseCtx = document.getElementById("BaseScenarioCashflowsGraph") as HTMLCanvasElement;
        const highCtx = document.getElementById("HighScenarioCashflowsGraph") as HTMLCanvasElement;
        const weightedCtx = document.getElementById("WeightedScenarioCashflowsGraph") as HTMLCanvasElement;

        if (lowCtx && !this.lowChart) {
            this.setupBarChart(this.lowScenarioCashflowsGraph$, lowCtx, chart => this.lowChart = chart);
        }
        if (baseCtx && !this.baseChart) {
            this.setupBarChart(this.baseScenarioCashflowsGraph$, baseCtx, chart => this.baseChart = chart);
        }
        if (highCtx && !this.highChart) {
            this.setupBarChart(this.highScenarioCashflowsGraph$, highCtx, chart => this.highChart = chart);
        }
        if (weightedCtx && !this.weightedChart) {
            this.setupBarChart(this.weightedScenarioCashflowsGraph$, weightedCtx, chart => this.weightedChart = chart);
        }
    }

    private setupBarChart(dataObservable$: Observable<DealCashflowGraphData>, canvas: HTMLCanvasElement, setChartInstance: (chart: Chart) => void) {
        this.subscriptions.push(
            dataObservable$.pipe(map(graphData => {
                if (canvas?.getContext("2d")) {
                    this.destroyExistingChart(canvas);

                    if (graphData && graphData.datasets.some(ds => ds.data && ds.data.length > 0)) {
                        const newChart = new Chart(canvas, this.getChartConfig(graphData));
                        setChartInstance(newChart);
                    }
                }
            })).subscribe()
        );
    }

    private destroyExistingChart(canvas: HTMLCanvasElement): void {
        if (canvas.id === "LowScenarioCashflowsGraph" && this.lowChart) {
            this.lowChart.destroy();
        }
        if (canvas.id === "BaseScenarioCashflowsGraph" && this.baseChart) {
            this.baseChart.destroy();
        }
        if (canvas.id === "HighScenarioCashflowsGraph" && this.highChart) {
            this.highChart.destroy();
        }
        if (canvas.id === "WeightedScenarioCashflowsGraph" && this.weightedChart) {
            this.weightedChart.destroy();
        }
    }

    private getChartConfig(graphData: DealCashflowGraphData): ChartConfiguration {
        return {
            type: "bar",
            data: {
                datasets: graphData.datasets as ChartDataset<"bar" | "line", { x: number; y: number }[]>[]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                animation: false,
                scales: {
                    y: {
                        beginAtZero: true,
                        stacked: true,
                        grid: {
                            color: (context) => context.tick.value === 0 ? "rgba(0,0,0,0.2)" : "rgba(0,0,0,0.1)",
                            lineWidth: (context) => context.tick.value === 0 ? 3 : 1
                        }
                    },
                    x: {
                        type: "time",
                        time: {
                            unit: "year", // Set the interval granularity; can also be set to quarter
                            displayFormats: {
                                quarter: "MMM yyyy"
                            },
                            tooltipFormat: "MMM yyyy"
                        },
                        ticks: {
                            source: "auto",
                        },
                        bounds: "ticks",
                        grid: {
                            offset: false
                        },
                        offset: false,
                    }
                },
                plugins: {
                    tooltip: {
                        callbacks: {
                            label: this.generateTooltipLabel(graphData)
                        }
                    },
                    legend: {
                        display: true
                    }
                }
            }
        };
    }

    private generateTooltipLabel(graphData: DealCashflowGraphData) {
        return (context: TooltipItem<"bar" | "line">) => {
            const dataPoint = context.raw as { x: number; y: number };

            const label = context.dataset.label ?? "";
            const value = dataPoint.y;
            const lines = [`${label}: ${value}`];

            if (context.dataset.label === "Asset Distribution") {
                const quarterDate = DateTime.fromMillis(dataPoint.x);

                const quarterLabel = quarterDate.toFormat("yyyy-MM-dd");
                const assetDetails = graphData.assetDistribution[quarterLabel];
                if (assetDetails && Object.keys(assetDetails).length > 0) {
                    Object.entries(assetDetails).forEach(([company, amount]) => {
                        lines.push(`${company}: ${amount}`);
                    });
                } else {
                    lines.push("No details available");
                }
            }
            return lines;
        };
    }

    private destroyCharts() {
        if (this.chart) {
            this.chart.destroy();
            this.chart = undefined;
        }
        if (this.lowChart) {
            this.lowChart.destroy();
            this.lowChart = undefined;
        }
        if (this.baseChart) {
            this.baseChart.destroy();
            this.baseChart = undefined;
        }
        if (this.highChart) {
            this.highChart.destroy();
            this.highChart = undefined;
        }
        if (this.weightedChart) {
            this.weightedChart.destroy();
            this.weightedChart = undefined;
        }
    }

    hasGraphData(graphData: DealCashflowGraphData): boolean {
        return graphData.datasets.some(ds => ds.data && ds.data.length > 0);
    }

    hasAggregateGraphData(graphData: { labels: Date[] }): boolean {
        return graphData.labels.length > 0;
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
        this.destroyCharts();
    }
}
